import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Crown, Clock, Sparkles, ShieldCheck, CalendarHeart, Flame, TrendingUp, ArrowUp } from 'lucide-react';
import { Card, CardTitle, CardDescription, Skeleton } from '@/components/ui';
import { SearchBar, ScoreBadge, CollectedCounter } from '@/components/keyword';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { formatNumber } from '@keywordgod/shared';
import { cn } from '@/lib/utils';
import { ROLE_COLOR, ROLE_LABEL, STATUS_COLOR, STATUS_LABEL } from '@/lib/constants';

export function Dashboard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  const greet =
    new Date().getHours() < 12
      ? '좋은 아침입니다'
      : new Date().getHours() < 18
      ? '좋은 오후입니다'
      : '좋은 저녁입니다';

  // 승인 대기 상태면 안내 카드만
  if (user.status === 'pending') return <PendingApprovalScreen />;
  if (user.status === 'suspended') return <SuspendedScreen />;

  return (
    <div className="space-y-8">
      <section>
        <p className="text-sm text-gray-400">{greet},</p>
        <h1 className="mt-1 flex items-center gap-3 text-3xl font-extrabold text-white">
          <span className="text-gold-gradient">{user.nickname || user.name}</span> 님
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-bold',
              ROLE_COLOR[user.role],
            )}
          >
            {ROLE_LABEL[user.role]}
          </span>
        </h1>
        <p className="mt-1 text-gray-500">어떤 키워드를 정복할까요?</p>

        <div className="mt-6 max-w-3xl">
          <SearchBar size="hero" />
        </div>

        <div className="mt-6 max-w-md">
          <CollectedCounter size="compact" />
        </div>
      </section>

      <RisingTicker />

      <SeasonStrip />

      <CategoryShortcuts />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GoldenTop5 />
        <PolicyTop5 />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NewsTop5 />
        <RecentHistory />
      </section>

      {(user.role === 'admin' || user.role === 'moderator') && <StaffShortcut />}
    </div>
  );
}

// ─── Phase 56: 급상승 키워드 티커 (미래에 터질 키워드 미리) ───

function RisingTicker() {
  const q = useQuery({
    queryKey: ['trending', 'tomorrow'],
    queryFn: () => api.trending.tomorrow(),
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const items = (q.data?.items ?? []).filter((it) => it.acc > 0).slice(0, 12);
  if (q.isError || items.length === 0) return null;

  return (
    <section className="rounded-xl border border-orange-400/30 bg-gradient-to-r from-orange-400/10 to-transparent px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Flame className="h-4 w-4 text-orange-400" />
        <span className="text-sm font-bold text-orange-200">지금 급상승 — 터지기 전에 선점</span>
        <Link to="/trending" className="ml-auto text-[11px] text-gold hover:underline">
          내일 핫 전체 →
        </Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {items.map((it) => {
          const pct = Math.round(it.acc * 100);
          return (
            <Link
              key={it.keyword}
              to={`/analyze/${encodeURIComponent(it.keyword)}`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-orange-400/40 bg-gray-950/60 px-3 py-1.5 text-sm hover:border-orange-400/80"
            >
              <span className="font-medium text-white">{it.keyword}</span>
              <span className="inline-flex items-center text-xs font-bold text-orange-300">
                <ArrowUp className="h-3 w-3" />
                {pct}%
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ─── Phase 50: 카테고리 빠른 진입 (쇼핑 편중 시각적 완화) ───

function CategoryShortcuts() {
  const policy: Array<{ id: string; label: string; emoji: string; description: string; tone: string }> = [
    {
      id: 'policy',
      label: '정책 / 지원금',
      emoji: '📋',
      description: '청년월세·부모급여·디딤돌·정부24',
      tone: 'border-emerald-400/40 bg-emerald-400/5 hover:border-emerald-400/70',
    },
    {
      id: 'news',
      label: '시사 / 경제',
      emoji: '📰',
      description: '금리·환율·물가·국제정세·AI',
      tone: 'border-blue-400/40 bg-blue-400/5 hover:border-blue-400/70',
    },
    {
      id: 'tax',
      label: '세금 / 환급',
      emoji: '💸',
      description: '연말정산·홈택스·종부세·세액공제',
      tone: 'border-amber-400/40 bg-amber-400/5 hover:border-amber-400/70',
    },
    {
      id: 'welfare',
      label: '공공복지',
      emoji: '🤝',
      description: '기초연금·의료급여·노인돌봄',
      tone: 'border-rose-400/40 bg-rose-400/5 hover:border-rose-400/70',
    },
  ];
  const content: Array<{ id: string; label: string; emoji: string; description: string; tone: string }> = [
    {
      id: 'drama',
      label: '드라마',
      emoji: '📺',
      description: '한드·미드·일드·OTT 시리즈',
      tone: 'border-purple-400/40 bg-purple-400/5 hover:border-purple-400/70',
    },
    {
      id: 'movie',
      label: '영화',
      emoji: '🎬',
      description: '박스오피스·OTT·마블·신작',
      tone: 'border-fuchsia-400/40 bg-fuchsia-400/5 hover:border-fuchsia-400/70',
    },
    {
      id: 'celeb',
      label: '스타·연예인',
      emoji: '⭐',
      description: 'K팝 아이돌·배우·예능인',
      tone: 'border-pink-400/40 bg-pink-400/5 hover:border-pink-400/70',
    },
    {
      id: 'sports',
      label: '스포츠',
      emoji: '⚽',
      description: '축구·야구·골프·올림픽',
      tone: 'border-sky-400/40 bg-sky-400/5 hover:border-sky-400/70',
    },
  ];
  return (
    <section className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-white">📡 시사·정책 카테고리</h2>
          <Link to="/categories" className="text-xs text-gold hover:underline">
            전체 카테고리 →
          </Link>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          쇼핑·뷰티만이 아니라 정책·세금·시사 키워드도 함께 분석할 수 있습니다.
        </p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {policy.map((c) => (
            <Link
              key={c.id}
              to={`/categories?cat=${c.id}`}
              className={cn('rounded-xl border px-3 py-3 transition-colors', c.tone)}
            >
              <div className="text-2xl">{c.emoji}</div>
              <div className="mt-1 text-sm font-bold text-white">{c.label}</div>
              <div className="mt-0.5 text-[11px] text-gray-400 leading-snug">{c.description}</div>
            </Link>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-white">🎭 콘텐츠 장르 카테고리</h2>
          <span className="rounded-full bg-purple-400/20 px-2 py-0.5 text-[10px] font-bold text-purple-200">
            NEW
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          블로그 마케터를 위한 드라마·영화·연예·스포츠 키워드 풀.
        </p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {content.map((c) => (
            <Link
              key={c.id}
              to={`/categories?cat=${c.id}`}
              className={cn('rounded-xl border px-3 py-3 transition-colors', c.tone)}
            >
              <div className="text-2xl">{c.emoji}</div>
              <div className="mt-1 text-sm font-bold text-white">{c.label}</div>
              <div className="mt-0.5 text-[11px] text-gray-400 leading-snug">{c.description}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 카드 1: 황금키워드 TOP 5 ────────────────────────────

function GoldenTop5() {
  const q = useQuery({ queryKey: ['dashboard', 'golden'], queryFn: () => api.golden.today() });
  const items = (q.data?.items ?? []).slice(0, 5);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-gold" /> 오늘의 황금키워드
          </CardTitle>
          <CardDescription>매일 자동 갱신 · 상위 5개</CardDescription>
        </div>
        <Link to="/golden" className="text-xs text-gold hover:underline">
          전체 보기 →
        </Link>
      </div>

      <div className="mt-4 space-y-2">
        {q.isLoading &&
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        {q.data && items.length === 0 && (
          <p className="text-sm text-gray-500">아직 데이터가 부족해요. 분석을 더 쌓아주세요.</p>
        )}
        {items.map((it, i) => (
          <Link
            key={it.keyword}
            to={`/analyze/${encodeURIComponent(it.keyword)}`}
            className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2.5 hover:border-gold/50"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="tabular text-xs text-gray-500 w-5">{i + 1}</span>
              <span className="truncate font-medium text-white">{it.keyword}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="tabular text-sm text-gray-300">{formatNumber(it.totalSearch)}</span>
              <ScoreBadge grade={it.grade} size="sm" />
            </div>
          </Link>
        ))}
      </div>
    </Card>
  );
}

// ─── Phase 50: 정책 / 시사 TOP 5 (쇼핑 편중 완화) ─────────

function CategoryTop5Card({
  catId,
  title,
  emoji,
  emptyMsg,
  tone,
}: {
  catId: string;
  title: string;
  emoji: string;
  emptyMsg: string;
  tone: string;
}) {
  const q = useQuery({
    queryKey: ['dashboard', 'cat', catId],
    queryFn: () => api.categories.keywords(catId, { limit: 5, minSearch: 100, includeUnanalyzed: false }),
    staleTime: 5 * 60 * 1000,
  });
  const items = q.data?.items ?? [];

  return (
    <Card className={cn('border', tone)}>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">{emoji}</span>
            <span>{title}</span>
          </CardTitle>
          <CardDescription>GodScore 순 · 최근 분석</CardDescription>
        </div>
        <Link to={`/categories?cat=${catId}`} className="text-xs text-gold hover:underline">
          더보기 →
        </Link>
      </div>

      <div className="mt-4 space-y-2">
        {q.isLoading &&
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        {q.data && items.length === 0 && (
          <p className="text-sm text-gray-500">{emptyMsg}</p>
        )}
        {items.map((it, i) => (
          <Link
            key={it.keyword}
            to={`/analyze/${encodeURIComponent(it.keyword)}`}
            className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2.5 hover:border-gold/50"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="tabular text-xs text-gray-500 w-5">{i + 1}</span>
              <span className="truncate font-medium text-white">{it.keyword}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="tabular text-sm text-gray-300">{formatNumber(it.totalSearch)}</span>
              <ScoreBadge grade={it.grade as 'S' | 'A' | 'B' | 'C' | 'D'} size="sm" />
            </div>
          </Link>
        ))}
      </div>
    </Card>
  );
}

function PolicyTop5() {
  return (
    <CategoryTop5Card
      catId="policy"
      title="📋 정책 / 지원금 TOP 5"
      emoji=""
      emptyMsg="정책 키워드 분석 진행 중 — 시드 800개 투입 후 cron이 순차 분석 중입니다 (수 분 후 표시)."
      tone="border-emerald-400/30 bg-emerald-400/5"
    />
  );
}

function NewsTop5() {
  return (
    <CategoryTop5Card
      catId="news"
      title="📰 시사 / 경제 TOP 5"
      emoji=""
      emptyMsg="시사 키워드 분석 진행 중 — 매일 새벽 뉴스 헤드라인에서 자동 추출됩니다."
      tone="border-blue-400/30 bg-blue-400/5"
    />
  );
}

// ─── 카드 2: 최근 검색 ──────────────────────────────────

function RecentHistory() {
  const q = useQuery({ queryKey: ['dashboard', 'history'], queryFn: () => api.me.history() });
  const items = (q.data ?? []).slice(0, 5);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-gold" /> 최근 검색
          </CardTitle>
          <CardDescription>방금 분석한 키워드들</CardDescription>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {q.isLoading &&
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        {q.data && items.length === 0 && (
          <p className="text-sm text-gray-500">
            아직 검색 이력이 없습니다. 첫 키워드를 분석해보세요!
          </p>
        )}
        {items.map((h) => {
          const failed = h.godScore == null && h.grade == null;
          return (
            <Link
              key={h.id}
              to={`/analyze/${encodeURIComponent(h.keyword)}`}
              className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2.5 hover:border-gold/50"
              title={failed ? '분석 실패 — 다시 시도하려면 클릭' : undefined}
            >
              <span
                className={cn(
                  'truncate font-medium',
                  failed ? 'text-gray-500' : 'text-white',
                )}
              >
                {h.keyword}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {h.godScore != null && (
                  <span className="tabular text-sm text-gray-300">God {h.godScore}</span>
                )}
                {h.grade && <ScoreBadge grade={h.grade} size="sm" />}
                {failed && (
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                    실패
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

// ─── 이번주 시즌 키워드 ────────────────────────────────

function SeasonStrip() {
  // Phase 34: 2달(D-60일) 전부터 표기. 종료된 시즌은 백엔드에서 자동 past 처리되어 제외됨.
  const q = useQuery({
    queryKey: ['dashboard', 'season-now-60'],
    queryFn: () => api.season.now(60),
  });
  const items = (q.data ?? []).slice(0, 6);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <CalendarHeart className="h-5 w-5 text-gold" /> 다가오는 시즌 키워드
          </CardTitle>
          <CardDescription>지금 진행 중 + 60일(2달) 내 다가오는 이벤트</CardDescription>
        </div>
        <Link to="/season" className="text-xs text-gold hover:underline">
          전체 보기 →
        </Link>
      </div>

      <div className="mt-4">
        {q.isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        )}
        {q.data && items.length === 0 && (
          <p className="text-sm text-gray-500">현재 진행 중이거나 60일 내 시작될 시즌이 없습니다.</p>
        )}
        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {items.map((ev) => {
              const visible = ev.keywords.slice(0, 3);
              const more = Math.max(0, ev.keywords.length - 3);
              const isActive = ev.state === 'active';
              return (
                <div
                  key={ev.id}
                  className={cn(
                    'rounded-xl border p-3',
                    isActive
                      ? 'border-emerald-400/40 bg-emerald-400/5'
                      : 'border-gold/30 bg-gold/5',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-lg shrink-0">{ev.emoji ?? '📅'}</span>
                      <span className="font-bold text-white text-sm truncate">{ev.name}</span>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 text-[10px] font-bold rounded-full px-2 py-0.5',
                        isActive
                          ? 'bg-emerald-400/15 text-emerald-300'
                          : 'bg-gold/15 text-gold',
                      )}
                    >
                      {isActive ? (
                        <span className="inline-flex items-center gap-1">
                          <Flame className="h-3 w-3" />
                          진행 중
                        </span>
                      ) : (
                        `D-${ev.daysUntilStart ?? 0}`
                      )}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {visible.map((k) => (
                      <Link
                        key={k}
                        to={`/analyze/${encodeURIComponent(k)}`}
                        className="rounded-full bg-gray-900 hover:bg-gold/15 hover:text-gold px-2 py-0.5 text-[11px] text-gray-300"
                      >
                        {k}
                      </Link>
                    ))}
                    {more > 0 && (
                      <span className="rounded-full bg-gray-950 px-2 py-0.5 text-[11px] text-gray-600">
                        +{more}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── 운영진 단축 ────────────────────────────────────────

function StaffShortcut() {
  const q = useQuery({
    queryKey: ['mod', 'stats'],
    queryFn: () => api.mod.stats(),
  });

  return (
    <Card className="border-gold/40 bg-gradient-to-r from-gold/10 to-transparent">
      <CardTitle className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-gold" /> 운영진 단축
      </CardTitle>
      <CardDescription>가입 대기 회원을 확인하세요.</CardDescription>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <StatPill label="가입 대기" value={q.data?.pending} highlight />
        <StatPill label="활성 회원" value={q.data?.active} />
        <StatPill label="정지" value={q.data?.suspended} />
        <StatPill label="운영진" value={(q.data?.admins ?? 0) + (q.data?.moderators ?? 0)} />
      </div>
      <div className="mt-4">
        <Link to="/mod" className="inline-flex items-center gap-1 text-sm text-gold hover:underline">
          <Sparkles className="h-3.5 w-3.5" /> 회원 승인 페이지로
        </Link>
      </div>
    </Card>
  );
}

function StatPill({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number | undefined;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        highlight && value && value > 0
          ? 'border-amber-400/50 bg-amber-400/10'
          : 'border-gray-800 bg-gray-950',
      )}
    >
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="tabular text-xl font-bold text-white">{value ?? '-'}</div>
    </div>
  );
}

// ─── 승인 대기 / 정지 화면 ──────────────────────────────

function PendingApprovalScreen() {
  const user = useAuthStore((s) => s.user)!;
  return (
    <div className="mx-auto max-w-2xl py-12">
      <Card className="text-center border-amber-400/40 bg-amber-400/5">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/20 mx-auto">
          <ShieldCheck className="h-7 w-7 text-amber-300" />
        </div>
        <CardTitle className="mt-4 text-amber-200">운영진 승인 대기 중</CardTitle>
        <CardDescription className="mt-2">
          챌린지 회원으로 등록되었습니다. 운영진이 승인하면 바로 키워드 분석을 시작할 수 있어요.
          승인까지 시간이 걸리면 챌린지 단톡방에서 문의해주세요.
        </CardDescription>
        <div className="mt-6 inline-flex items-center gap-2">
          <span
            className={cn(
              'rounded-full px-3 py-1 text-xs font-bold',
              STATUS_COLOR[user.status],
            )}
          >
            {STATUS_LABEL[user.status]}
          </span>
          <span className="text-xs text-gray-500">{user.email}</span>
        </div>
      </Card>
    </div>
  );
}

function SuspendedScreen() {
  const user = useAuthStore((s) => s.user)!;
  return (
    <div className="mx-auto max-w-2xl py-12">
      <Card className="text-center border-red-500/40 bg-red-500/5">
        <CardTitle className="text-red-300">계정 이용이 정지되었습니다</CardTitle>
        <CardDescription className="mt-2">
          정지 사유는 챌린지 운영진에 문의해주세요.
        </CardDescription>
        <div className="mt-4 text-xs text-gray-500">{user.email}</div>
      </Card>
    </div>
  );
}
